import React, { Component } from 'react';
import { Link } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faExternalLink, faPhone } from '@fortawesome/pro-light-svg-icons';
import PortalTooltip from './PortalTooltip';

class PortalLink extends Component {
  getCleanString(value) {
    return value === undefined || value === null ? '' : String(value).trim();
  }

  getStaticTextFromChildren(children) {
    const textParts = [];

    React.Children.forEach(children, (child) => {
      if (typeof child === 'string' || typeof child === 'number') {
        textParts.push(this.getCleanString(child));
      } else if (React.isValidElement(child)) {
        textParts.push(this.getStaticTextFromChildren(child.props.children));
      }
    });

    return textParts.filter(Boolean).join(' ').trim();
  }

  getAltTextFromChildren(children) {
    const altParts = [];

    React.Children.forEach(children, (child) => {
      if (React.isValidElement(child)) {
        altParts.push(this.getCleanString(child.props.alt));
        altParts.push(this.getAltTextFromChildren(child.props.children));
      }
    });

    return altParts.filter(Boolean).join(' ').trim();
  }

  classNameHasToken(className, token) {
    return String(className || '')
      .split(/\s+/)
      .some((name) => name === token);
  }

  //the icons are handled this way instead of using the traditional
  //::before tags and adding a font awesome icon to them is that
  //MUI refuses to honor text-decoration rules on links with ::before or ::after psuedo elements
  //so we have to add the icons in the dom instead of css.
  //This is a known issue with MUI and there are no current workarounds
  //meh.
  getIcons(className) {
    const icons = {
      before: null,
      after: null,
    };

    if (!this.classNameHasToken(className, 'noIcon')) {
      if (this.classNameHasToken(className, 'phone')) {
        icons.before = faPhone;
      } else if (this.classNameHasToken(className, 'email')) {
        icons.before = faEnvelope;
      } else if (this.classNameHasToken(className, 'newTab')) {
        icons.after = faExternalLink;
      }
    }

    return icons;
  }

  render() {
    const {
      href,
      target,
      title,
      className,
      underline,
      sx,
      id,
      children,
      'aria-label': ariaLabel,
      ariaLabel: ariaLabelProp,
      forwardedRef,
      ...otherProps
    } = this.props;

    const isExternalLink = target === '_blank' || target === '_new';

    //in reality the myOdu__link class has nothing in it
    //but keep it here for the future when MUI fixes its
    //text-decoratio issue
    const linkClassName =
      'myOdu__link ' +
        //if its external concat externalLink class
        [isExternalLink ? 'newTab ' : '', className]
          .filter((name) => name && String(name).trim())
          .join(' ') || undefined;

    const titleText = this.getCleanString(title);

    //If the aria-label/ariaLabel is passed, it wins
    //Otherise, get it from the title
    //if the title is not passed, make it from the statuic text of the children
    //now if theres no static text, grab alt tags
    //now if theres no alt tags, just use a generic "link"
    const linkLabel =
      this.getCleanString(ariaLabel) ||
      this.getCleanString(ariaLabelProp) ||
      titleText ||
      this.getStaticTextFromChildren(children) ||
      this.getAltTextFromChildren(children) ||
      'link';

    const titleLabel = titleText || linkLabel;
    const displayAriaLabel = isExternalLink ? `${linkLabel} (opens in a new tab)` : linkLabel;
    const displayTitle = isExternalLink ? `${titleLabel} (opens in a new tab)` : titleLabel;

    //if theres no id passed, make one from the label (using the logic above to get the label text)
    const generatedId = linkLabel.replace(/[^\w-]/g, '');
    const linkId = this.getCleanString(id) || `link__${generatedId}`;
    const icons = this.getIcons(linkClassName);

    //wrap the children in a span so the tooltip doesnt go 100% width
    //but check to make sure its a legit element
    const tooltipContent = React.isValidElement(children) ? children : <span>{children}</span>;

    const link = (
      <Link
        {...otherProps}
        ref={forwardedRef}
        id={linkId}
        href={href}
        target={target}
        rel={isExternalLink ? 'noopener noreferrer' : undefined}
        underline={underline}
        sx={sx}
        className={linkClassName}
        aria-label={displayAriaLabel}
      >
        <PortalTooltip id={`${linkId}__tooltip`} title={displayAriaLabel}>
          {tooltipContent}
        </PortalTooltip>
      </Link>
    );

    return link;
  }
}

export default React.forwardRef((props, ref) => <PortalLink {...props} forwardedRef={ref} />);
